import { useEffect, useRef, useState } from 'hooks';
import dsp from './dsp';

const cutoffFrequency = 0.5; // cutoff frequency in Hz
const samplingFrequency = 512; // sampling frequency in Hz
const a = 0.99; // 1 - Math.exp(-2 * Math.PI * cutoffFrequency / samplingFrequency);
let x_prev = 0; // previous input sample
let y_prev = 0; // previous output sample

function highPassFilterECG(x) {
  // compute current output sample
  const y = a * y_prev + a * (x - x_prev);
  // update previous input and output samples
  x_prev = x;
  y_prev = y;
  // return current output
  return y;
}

export function removeDCBias(ecgSignal) {
  const filteredECG = [];
  for (let i = 0; i < ecgSignal.length; i++) {
    filteredECG[i] = highPassFilterECG(ecgSignal[i]);
  }
  x_prev = 0;
  y_prev = 0;

  return filteredECG;
}
//   function findPeaks(signal) {
//     const peaksArray = scipy.signal.find_peaks(signal)
//     return peaksArray
//   }
function findMaxPeak(signal) {
  // Set the initial maximum peak to the first sample
  let maxPeak = signal[0];

  // Loop through the signal and find the maximum peak
  for (let i = 1; i < signal.length; i++) {
    if (signal[i] > maxPeak) {
      maxPeak = signal[i];
    }
  }

  return maxPeak;
}
function lerp(a, b, ratio) {
  return a + (b - a) * ratio;
}

function clamp(v, min, max) {
  return Math.max(Math.min(v, max), min);
}

export function leftPadSamples(samples, count) {
  const missingSampleCount = Math.max(count - samples.length, 0);
  const paddingZeros = new Array(missingSampleCount).fill(0);

  return paddingZeros.concat(samples);
}

export function rightPadSamples(samples, count) {
  const missingSampleCount = Math.max(count - samples.length, 0);
  const paddingZeros = new Array(missingSampleCount).fill(0);

  return samples.concat(paddingZeros);
}

function downSample(samples, count, sampleRate) {
  if (count <= 0) {
    return [];
  } else if (samples.length <= count) {
    return samples;
  }

  const stride = samples.length / count;
  const newSamples = [];

  const inputRate = 100;
  const outputRate = inputRate / stride;

  const lowPass = new dsp.Biquad(0, sampleRate);
  lowPass.setF0(outputRate / 2);
  const filteredSamples = lowPass.process(samples);

  for (let i = 0; i < filteredSamples.length; i += stride) {
    const idx = Math.floor(i);
    newSamples.push(filteredSamples[idx]);
  }

  return newSamples;
}

function upSample(samples, count, sampleRate) {
  if (count <= 0) {
    return [];
  } else if (samples.length >= count) {
    return samples;
  }

  const stride = count / samples.length;
  const newSamples = [];

  for (let i = 0; i < count; i++) {
    const baseIndex = Math.floor(i / stride);

    const a = samples[baseIndex];
    const b = samples[clamp(baseIndex + 1, 0, samples.length - 1)];
    const ratio = (i % stride) / stride;

    const interp = lerp(a, b, ratio);

    newSamples.push(interp);
  }

  return newSamples;
}

export function resample(samples, count, sampleRate) {
  if (samples.length < count) {
    return upSample(samples, count, sampleRate);
  } else if (samples.length > count) {
    return downSample(samples, count, sampleRate);
  } else {
    return samples;
  }
}

export function resampleECG(signal, originalHz, targetHz) {
  // Calculate the ratio of original frequency to target frequency
  const ratio = targetHz / originalHz;

  // Initialize an array to store resampled signal
  const resampledSignal = [];

  // Iterate over the original signal
  for (let i = 0; i < signal.length - 1; i++) {
    // Calculate the fractional index in the resampled signal
    const index = i * ratio;

    // Calculate the integer and fractional parts of the index
    const integerPart = Math.floor(index);
    const fractionalPart = index - integerPart;

    // Perform linear interpolation to resample the signal
    const interpolatedValue = signal[integerPart] + (signal[integerPart + 1] - signal[integerPart]) * fractionalPart;

    if (index > signal.length - 1) {
      return resampledSignal;
    }
    // Push the interpolated value to the resampled signal
    resampledSignal.push(interpolatedValue);
  }

  return resampledSignal;
}

// https://reactjs.org/blog/2015/12/16/ismounted-antipattern.html
export function makeCancellable(promise) {
  let hasCanceled_ = false;

  const wrappedPromise = new Promise((resolve, reject) => {
    promise.then(
      (val) => (hasCanceled_ ? reject({ isCanceled: true }) : resolve(val)),
      (error) => (hasCanceled_ ? reject({ isCanceled: true }) : reject(error)),
    );
  });

  return {
    promise: wrappedPromise,
    cancel() {
      hasCanceled_ = true;
    },
  };
}

// A hook to automatically cancel cancellables when the component unmounts
export function useCleanupOnUnmount() {
  const cancellables = useRef([]);
  useEffect(() => () => cancellables.current.forEach((cancellable) => cancellable.cancel()), []);

  return (promise) => {
    const cancellable = makeCancellable(promise);
    cancellables.current = cancellables.current.concat(cancellable);
  };
}

// https://reactjs.org/docs/hooks-faq.html#how-to-get-the-previous-props-or-state
export function usePrevious(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });

  return ref.current;
}

// https://stackoverflow.com/a/53837442
export function useForceUpdate() {
  const [, setValue] = useState(0);

  return () => setValue((setVal) => setVal + 1);
}

export { storage } from './storage/storage';
